import { useCallback } from 'react';
import { useTaggableTableRowActions } from '@/features/shared/hooks/useTaggableTableRowActions';
import { useDeleteSuite, useUpdateSuite } from '@/features/suites/hooks/suites';
import type { TagColor } from '@/shared/data/tags';
import type { SuiteListItemDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type UseSuitesTableRowActionsParams = { selectedProjectId: string | null };

type UseSuitesTableRowActionsResult = {
  deleteTarget: DeleteTarget | null;
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteListItemDto) => void;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onUpdateRowTagsColor: (input: {
    row: SuiteListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
};

export const useSuitesTableRowActions = ({
  selectedProjectId,
}: UseSuitesTableRowActionsParams): UseSuitesTableRowActionsResult => {
  const deleteSuiteMutation = useDeleteSuite();
  const updateSuiteMutation = useUpdateSuite();
  const updateRowTags = useCallback(
    async ({ row, tags }: { row: SuiteListItemDto; tags: string[] }) => {
      await updateSuiteMutation.mutateAsync({ id: row.id, tags });
    },
    [updateSuiteMutation],
  );

  return useTaggableTableRowActions({
    deleteMutation: deleteSuiteMutation,
    entity: 'suite',
    selectedProjectId,
    updateRowTags,
    updateRowTagsPending: updateSuiteMutation.isPending,
  });
};
